import { FC, useEffect, useState } from 'react';
import { Prompt } from 'react-router-dom';
import * as vp from 'vopay';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useBuildBrandingFormRows } from './buildBrandingFormRows.helper';
import styles from '../BrandingSettings.module.scss';
import { usePendingData } from '@/providers/PendingData.provider';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';

const { Box, Button, Checkbox, TextField, Icon, InputFile, ElementContainer, Description, UserNavPromptDialogue } = vp;

export type BrandingValues = {
  Logo: string;
  SmallLogo: string;
  PrimaryColor: string;
  SecondaryColor: string;
  ApplyToAll: boolean;
};

type BrandingFormProps = {
  defaultValues: BrandingValues;
  reset: boolean;
  brandingState: vp.BrandingProductType;
  onChange: (values: BrandingValues) => void;
  onSubmit: (values: BrandingValues) => void;
};

const fileErrorDefaults: Record<vp.BrandingId, string> = {
  [vp.BrandingId.Logo]: '',
  [vp.BrandingId.SmallLogo]: '',
  [vp.BrandingId.PrimaryColor]: '',
  [vp.BrandingId.SecondaryColor]: '',
  [vp.BrandingId.ApplyToAll]: '',
};

const BrandingForm: FC<BrandingFormProps> = ({ defaultValues, reset, brandingState, onSubmit, onChange }) => {
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const { hasPendingData, updatePendingData, initPendingData, reset: resetPendingData } = usePendingData();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [fileErrors, setFileErrors] = useState(fileErrorDefaults);
  const brandingRow = useBuildBrandingFormRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.BrandingSettings.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  useEffect(() => {
    initPendingData(defaultValues);
  }, [defaultValues]);

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={(values) => {
        onSubmit(values);
        resetPendingData();
        setIsUpdate(false);
        setFileErrors(fileErrorDefaults);
      }}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit, handleReset }) => {
        const BrandingRows = brandingRow.map(({ label, id, type }) => {
          const maxSizes = getMaxSize(brandingState);
          return getField(id, label, type, isUpdate, maxSizes.maxHeight, maxSizes.maxWidth, brandingState);
        });

        useEffect(() => {
          updatePendingData(values);
          onChange(values);
        }, [values]);

        useEffect(() => {
          if (reset) {
            onChange(values);
            updatePendingData(defaultValues);
            handleReset();
            setIsUpdate(false);
            setFileErrors(fileErrorDefaults);
          }
        }, [reset]);

        return (
          <form onSubmit={handleSubmit}>
            {...BrandingRows}

            <Box mt="md" jc="fe" gapSize="12px" pt="lg">
              {!isUpdate && (
                <Button
                  id="Branding-update"
                  type="button"
                  isDisabled={isRoleViewOnly}
                  label={tGlobal('Buttons.edit')}
                  onClick={() => {
                    setIsUpdate(true);
                  }}
                />
              )}
              {isUpdate && (
                <Button
                  id="Branding-cancel"
                  type="button"
                  label={tGlobal('Buttons.cancel')}
                  color="secondary"
                  onClick={() => {
                    if (hasPendingData) {
                      setShowPrompt(true);
                    } else {
                      handleReset();
                      setIsUpdate(false);
                      setFileErrors(fileErrorDefaults);
                    }
                  }}
                />
              )}
              {isUpdate && <Button id="Branding-save" type="submit" label={tGlobal('Buttons.save')} />}
            </Box>
            <UserNavPromptDialogue
              message={t('unsavedPromptMessage')}
              onConfirm={() => {
                handleReset();
                setIsUpdate(false);
                setFileErrors(fileErrorDefaults);
                updatePendingData(defaultValues);
                setShowPrompt(false);
              }}
              onCancel={() => {
                setShowPrompt(false);
              }}
              visible={showPrompt}
            />
            <Prompt when={hasPendingData} message={t('unsavedPromptMessage')} />
          </form>
        );
        function getField(
          id: vp.BrandingId,
          label: string,
          type: 'checkbox' | 'color' | 'inputfile',
          update: boolean,
          maxHeight: number,
          maxWidth: number,
          brandingStateInner: vp.BrandingProductType,
        ) {
          switch (type) {
            case 'color':
              return (
                <ElementContainer
                  label={
                    <Box>
                      {label}
                      {getRequiredIcon(update)}
                    </Box>
                  }
                  padding="20px 0 10px"
                  isBold={false}
                  input={
                    <Box ai="fs" gapSize="8px">
                      <div
                        className={styles.colorBox}
                        style={{
                          background: `${vp.validateAndReturn(defaultValues[id] as string, values[id] as string)}`,
                        }}
                      />
                      <TextField
                        preset="text"
                        key={id}
                        name={id}
                        width="100%"
                        variant="outlined"
                        containerWidth="100%"
                        disabled={!update}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        height="36px"
                        value={values[id] as string}
                        error={errors[id] && touched[id] ? errors[id] : null}
                      />
                    </Box>
                  }
                />
              );
            case 'inputfile':
              return (
                <ElementContainer
                  label={
                    <Box>
                      {label}
                      {getRequiredIcon(update)}
                    </Box>
                  }
                  isBold={false}
                  padding="0px 0 12px"
                  input={
                    <>
                      <Box gapSize="28px" mb="sm" jc="sb" ai="center">
                        {values[id] !== '' && (
                          <Box ai="center">
                            <img
                              style={{
                                backgroundColor:
                                  brandingStateInner === vp.BrandingProductType.Elinx ||
                                  brandingStateInner === vp.BrandingProductType.Email
                                    ? '#FBFBFB'
                                    : vp.validateAndReturn(defaultValues.SecondaryColor, values.SecondaryColor),
                                padding: '10px 14px',
                              }}
                              className={styles.logo}
                              src={values[id] as string}
                              alt="logo"
                            />
                          </Box>
                        )}
                        {update && (
                          <div>
                            <InputFile
                              id={id}
                              key={id}
                              name={id}
                              variant="standard"
                              onBlur={handleBlur}
                              onChange={(e) => {
                                if (e.target.value !== '') {
                                  handleChange(e);
                                }
                              }}
                              accept="image/svg+xml"
                              validExtensions={['svg']}
                              maxSize={20}
                              label={t('inputFileLabel')}
                              value={values[id] as string}
                              hideLabel
                              bold
                              onBlurFileName={handleBlur}
                              onChangeFileName={handleChange}
                              maxHeight={maxHeight}
                              maxWidth={maxWidth}
                              invalidFileFormatMsg={t('invalidFileFormatMsg')}
                              onValidation={(_, errorMessage) => {
                                setFileErrors({ ...fileErrors, [id]: errorMessage });
                              }}
                              error={errors[id] && touched[id] ? errors[id] : null}
                            />
                          </div>
                        )}
                      </Box>
                      {update && (
                        <Box width="100%" pt="sm">
                          {!errors[id] && !fileErrors[id] && (
                            <Description color="#4D4D4D">{t('updateFileValidation')}</Description>
                          )}
                          {errors[id] && (
                            <Description color="#D22D2D" size="sm">
                              {errors[id]}
                            </Description>
                          )}
                          {fileErrors[id] && (
                            <Description color="#D22D2D" size="sm">
                              {fileErrors[id]}
                            </Description>
                          )}
                        </Box>
                      )}
                    </>
                  }
                />
              );
            default:
              return (
                <>
                  {update && (
                    <Box pt="md" gapSize="8px">
                      <Checkbox
                        key={id}
                        name={id}
                        value={values[id] as boolean}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        hasNoPadding
                        error={errors[id] && touched[id] ? errors[id] : null}
                      />
                      {label}
                    </Box>
                  )}
                </>
              );
          }
        }
      }}
    </Formik>
  );

  function createdValidationSchema() {
    const hexColorRegex = /^#[0-9a-fA-F]{6}$/;
    return Yup.object().shape({
      PrimaryColor: Yup.string().matches(hexColorRegex, t('hexValidation')).required(),
      SecondaryColor: Yup.string().matches(hexColorRegex, t('hexValidation')).required(),
      Logo: Yup.string().required(),
    });
  }
  function getRequiredIcon(isRequired: boolean): React.JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" height="8px" width="8px" color="error" /> : <></>;
  }
  function getMaxSize(type: vp.BrandingProductType): { maxWidth: number; maxHeight: number } {
    switch (type) {
      case vp.BrandingProductType.Email:
        return { maxWidth: 123, maxHeight: 75 }; // Adjust values as needed
      case vp.BrandingProductType.Elinx:
        return { maxWidth: 175, maxHeight: 100 }; // Adjust values as needed
      case vp.BrandingProductType.Portal:
        return { maxWidth: 150, maxHeight: 110 }; // Adjust values as needed
      case vp.BrandingProductType.Onboarding:
        return { maxWidth: 250, maxHeight: 36 }; // Adjust values as needed
      default:
        throw new Error('Invalid branding type');
    }
  }
};

export default BrandingForm;
